import { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import dayjs from 'dayjs';
import {
    setCheckInDate,
    setCheckOutDate,
    setCity,
    setEndTime,
    setSelectedApartment,
    setStartTime,
} from '../../../store/slices/bookingCreationSlice';
import { Apartment } from '../../../types/bookingCreation';
import { DepotLocation } from '../../../types/locations';
import { SLOT_PREFILL_PARAMS, parseSlotPrefill } from './slotPrefill';
import { useLocationTimeZone } from '../../../hooks/useLocationTimeZone';

// Applies a slot picked in the operator week calendar (#3039) to the venue
// creation screen: dates, times, city, and — once
// the screen's own availability search has run — the object itself.
//
// The object cannot be set directly. The screens select from the list their
// search returns, and that search needs a guest count the calendar does not
// know; prefilling one would be a number nobody chose. So the object is
// selected the moment it appears in that list, and until then the screen says
// why it is not selected yet — never silently empty.

export type SlotPrefillStatus =
    /** No prefill, or the operator has since picked a different object. */
    | 'none'
    /** The search waits for the guest count. */
    | 'awaiting_guests'
    | 'searching'
    | 'selected'
    /** The search ran and the object is not in it: taken, or too small. */
    | 'unavailable'
    /** The object's location is not among the screen's locations. */
    | 'location_unknown';

interface UseSlotPrefillArgs {
    locations: DepotLocation[] | undefined;
    apartments: Apartment[] | null | undefined;
    loadingApartments: boolean;
    selectedApartment: Apartment | null | undefined;
    numberOfGuests: number | null | undefined;
}

/**
 * Must be called AFTER the screen's mount effect that clears the booking
 * slice: effects run in call order, so this one lands on the cleared state.
 */
export const useSlotPrefill = ({
    locations,
    apartments,
    loadingApartments,
    selectedApartment,
    numberOfGuests,
}: UseSlotPrefillArgs): SlotPrefillStatus => {
    const dispatch = useDispatch();
    const [searchParams] = useSearchParams();
    // #3219: the slot's wall clock is its location's — from the admin
    // location list, else from the screen's own list row.
    const prefillLocationId = searchParams.get(SLOT_PREFILL_PARAMS.locationId);
    const prefillZone = useLocationTimeZone(
        prefillLocationId,
        locations?.find((location) => location.id === prefillLocationId)?.timeZone,
    );
    const prefill = useMemo(() => parseSlotPrefill(searchParams, prefillZone), [searchParams, prefillZone]);

    // Applied ONCE per distinct prefill (the link's query string), and only
    // once the screen's location list has loaded: the zone the slot is read
    // on comes from it, and a list arriving after the operator has edited the
    // form must not write the link's values over those edits (#3219).
    // The applied key is released in the cleanup so StrictMode's second mount
    // — which runs the screen's clearing effect again — re-applies it.
    const prefillKey = prefill ? searchParams.toString() : null;
    const locationsLoaded = locations !== undefined;
    const appliedKey = useRef<string | null>(null);
    const latestPrefill = useRef(prefill);
    latestPrefill.current = prefill;
    useEffect(() => {
        const current = latestPrefill.current;
        if (!current || !prefillKey || !locationsLoaded || appliedKey.current === prefillKey) return undefined;
        appliedKey.current = prefillKey;
        dispatch(setCheckInDate({ checkInDate: dayjs(current.startDate) }));
        dispatch(setCheckOutDate({ checkOutDate: dayjs(current.endDate) }));
        dispatch(setStartTime({ startTime: current.startTime }));
        dispatch(setEndTime({ endTime: current.endTime }));
        return () => {
            appliedKey.current = null;
        };
    }, [prefillKey, locationsLoaded, dispatch]);

    const prefillCity = useMemo(() => {
        if (!prefill?.locationId || !locations) return undefined;
        return locations.find((location) => location.id === prefill.locationId)?.address?.city ?? null;
    }, [prefill, locations]);

    useEffect(() => {
        if (prefillCity) dispatch(setCity({ city: prefillCity }));
    }, [prefillCity, dispatch]);

    // Once the operator picks a different object, the prefill stops steering.
    const overridden = useRef(false);
    if (prefill && selectedApartment && selectedApartment.assetId !== prefill.assetId) {
        overridden.current = true;
    }

    const match = prefill
        ? apartments?.find((apartment) => apartment.assetId === prefill.assetId)
        : undefined;

    useEffect(() => {
        if (overridden.current || selectedApartment || !match) return;
        dispatch(setSelectedApartment(match));
    }, [match, selectedApartment, dispatch]);

    // "Not in the list" means something only after a search has actually run;
    // before that the list is the empty one the screen starts with.
    const [searched, setSearched] = useState(false);
    useEffect(() => {
        if (loadingApartments) setSearched(true);
    }, [loadingApartments]);

    if (!prefill || overridden.current) return 'none';
    if (selectedApartment?.assetId === prefill.assetId) return 'selected';
    if (prefillCity === null) return 'location_unknown';
    if (!numberOfGuests) return 'awaiting_guests';
    if (loadingApartments || !searched || match) return 'searching';
    return 'unavailable';
};
